// src/components/managed-sources/GenericTransformAuthStep.tsx

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  List,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconDatabase,
  IconPlugConnected,
  IconSearch,
  IconX,
} from "@tabler/icons-react";

import { discoverSourceTables, type SourceTableDiscoveryResult } from "@/api/managedSources";
import { useBranding } from "@/context/BrandingContext";
import BqSourceSelector from "./BqSourceSelector";

type Props = {
  sourceProjectId: string;
  sourceDatasetId: string;
  connectorType: string;
  onConfigChange: (projectId: string, datasetId: string) => void;
  onValidated: (valid: boolean) => void;
  onAvailableCategories?: (categories: string[]) => void;
};

export default function GenericTransformAuthStep({
  sourceProjectId,
  sourceDatasetId,
  connectorType,
  onConfigChange,
  onValidated,
  onAvailableCategories,
}: Props) {
  const { branding } = useBranding();
  const [discovery, setDiscovery] = useState<SourceTableDiscoveryResult | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [hasDiscovered, setHasDiscovered] = useState(false);

  // Validation: requires successful discovery with at least one available category
  useEffect(() => {
    const valid =
      !!sourceProjectId &&
      !!sourceDatasetId &&
      hasDiscovered &&
      (discovery?.available_categories?.length ?? 0) > 0;
    onValidated(valid);
  }, [sourceProjectId, sourceDatasetId, hasDiscovered, discovery, onValidated]);

  function handleConfigChange(projectId: string, datasetId: string) {
    onConfigChange(projectId, datasetId);
    // Reset discovery when source changes
    setDiscovery(null);
    setHasDiscovered(false);
    setDiscoverError(null);
  }

  async function handleDiscover() {
    if (!sourceProjectId || !sourceDatasetId) return;
    setDiscovering(true);
    setDiscoverError(null);
    setDiscovery(null);
    setHasDiscovered(false);

    try {
      const result = await discoverSourceTables(sourceProjectId, sourceDatasetId, connectorType);
      setDiscovery(result);
      setHasDiscovered(true);
      onAvailableCategories?.(result.available_categories);
    } catch (e: any) {
      const msg = e?.message || "Failed to verify source data";
      if (msg.includes("no access") || msg.includes("permission") || msg.includes("Grant BigQuery")) {
        setDiscoverError(
          "Permission denied. Make sure the runtime service account has BigQuery Data Viewer access on the source project."
        );
      } else if (msg.includes("not found") || msg.includes("not accessible")) {
        setDiscoverError(
          "Dataset not found or not accessible. Please check the project and dataset selection."
        );
      } else {
        setDiscoverError(msg);
      }
    } finally {
      setDiscovering(false);
    }
  }

  const totalCategories =
    (discovery?.available_categories?.length ?? 0) +
    (discovery?.unavailable_categories?.length ?? 0);

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="violet">
          <IconDatabase size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect your BigQuery data
          </Text>
          <Text size="sm" c="dimmed">
            Point {branding.productName} to your existing data in BigQuery. {branding.productName} will
            transform and model it for analytics.
          </Text>
        </Stack>
      </Group>

      <BqSourceSelector
        sourceProjectId={sourceProjectId}
        sourceDatasetId={sourceDatasetId}
        onConfigChange={handleConfigChange}
      />

      {sourceProjectId && sourceDatasetId && (
        <Button
          variant="light"
          leftSection={<IconSearch size={16} />}
          onClick={handleDiscover}
          loading={discovering}
          size="sm"
        >
          Verify source data
        </Button>
      )}

      {discoverError && (
        <Alert color="red" icon={<IconX size={16} />}>
          {discoverError}
        </Alert>
      )}

      {/* Discovery results */}
      {hasDiscovered && discovery && (
        <>
          {/* Available categories */}
          {discovery.available_categories.length > 0 && (
            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                borderColor: "var(--mantine-color-green-4)",
                backgroundColor: "var(--mantine-color-green-0)",
              }}
            >
              <Group gap="sm" mb={discovery.unavailable_categories.length > 0 ? "xs" : 0}>
                <ThemeIcon size={32} color="green" variant="light" radius="xl">
                  <IconPlugConnected size={16} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text size="sm" fw={600}>
                    {discovery.available_categories.length} of {totalCategories} categories available
                  </Text>
                  <Text size="xs" c="dimmed">
                    {sourceProjectId}.{sourceDatasetId}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          )}

          {/* No categories available */}
          {discovery.available_categories.length === 0 && (
            <Alert color="red" icon={<IconX size={16} />}>
              No data categories could be matched. The source dataset does not
              contain the expected table names. Make sure your source tables use
              {branding.productName}&apos;s naming convention (e.g. <code>fb_ad_performance_daily</code>).
            </Alert>
          )}

          {/* Unavailable categories with missing tables */}
          {discovery.unavailable_categories.length > 0 && (
            <Paper withBorder p="md" radius="md">
              <Stack gap="xs">
                <Text size="sm" fw={600} c="orange">
                  {discovery.unavailable_categories.length}{" "}
                  {discovery.unavailable_categories.length === 1
                    ? "category"
                    : "categories"}{" "}
                  unavailable — missing source tables
                </Text>
                {discovery.unavailable_categories.map((cat) => (
                  <Stack key={cat.key} gap={2}>
                    <Text size="sm" fw={500}>
                      {cat.label}
                    </Text>
                    <List size="xs" c="dimmed" withPadding>
                      {cat.missing_tables.map((t) => (
                        <List.Item key={t}>
                          <code>{t}</code>
                        </List.Item>
                      ))}
                    </List>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
